/**
 * THE MODULE MANIFEST — the single source of truth for authorization.
 *
 * Nothing else in the platform may define a module, an action, an access level or
 * a special action. Route guards name an action from this table; the role editor
 * renders this table; the migration derives grants from this table; CI fails if
 * anything drifts from this table.
 *
 * Reading order for a reviewer:
 *   1. every module declares ALL FOUR levels (none/view/edit/full), so the
 *      administrator-facing model is uniform;
 *   2. `full` lists its actions explicitly — it is never "everything", so an
 *      action added later is unreachable until assigned deliberately;
 *   3. special actions are listed OUTSIDE the levels and are never implied by
 *      one; each names the module baseline it additionally requires.
 *
 * Validation lives in ./authz-validate.ts and runs at import, at API startup and
 * in CI.
 */
import type {
  ModuleDefinition,
  SpecialActionDefinition,
} from "@yazamco/shared-types";

/** Terser literal for a special action; `label` doubles as the UI checkbox. */
function special(
  key: string,
  requires_level: SpecialActionDefinition["requires_level"],
  label: string,
  description: string,
): SpecialActionDefinition {
  return { key, requires_level, label, description };
}

export const MODULE_MANIFEST = [
  // ══ Overview ═══════════════════════════════════════════════════════════════
  {
    key: "dashboard",
    display_name: "Dashboard",
    category: "Overview",
    description: "The operations dashboard: platform health and inventory counters.",
    actions: ["dashboard.read"],
    access_levels: {
      none: [],
      view: ["dashboard.read"],
      edit: ["dashboard.read"],
      full: ["dashboard.read"],
    },
    special_actions: [],
  },

  // ══ CRM ════════════════════════════════════════════════════════════════════
  {
    key: "customers",
    display_name: "Customers",
    category: "CRM",
    description: "The customer directory and asynchronous provisioning.",
    actions: [
      "customers.read",
      "customers.upsert",
      "customers.provisioning_callback",
      "customers.bulk_import",
      "customers.read_provisioning_secret",
      "customers.email_credentials",
    ],
    access_levels: {
      none: [],
      view: ["customers.read"],
      edit: ["customers.read", "customers.upsert"],
      full: ["customers.read", "customers.upsert"],
    },
    special_actions: [
      // The provisioning callback is a SPECIAL action, not part of a level: it is
      // the surface the external pipeline reports INTO, and no human level should
      // confer it. Only `svc_n8n_provisioning` is granted it, and only its
      // credential reaches the route.
      special(
        "customers.provisioning_callback",
        "view",
        "Provisioning callback",
        "Report the result of a provisioning job. Held by the provisioning service principal, not by people.",
      ),
      special(
        "customers.bulk_import",
        "edit",
        "Bulk import",
        "Provision many customers in one request. Each row fans out to an external provisioning pipeline.",
      ),
      special(
        "customers.read_provisioning_secret",
        "edit",
        "Read provisioning credentials",
        "Read a provisioning job, which carries the one-time credentials created for the customer.",
      ),
      special(
        "customers.email_credentials",
        "edit",
        "Email provisioning result",
        "Send the provisioning result, including credentials, to an external recipient.",
      ),
    ],
  },
  {
    key: "services",
    display_name: "Service Catalog",
    category: "CRM",
    description: "The catalog of systems that customers can be subscribed to.",
    actions: [
      "services.read",
      "services.create",
      "services.update",
      "services.delete",
    ],
    access_levels: {
      none: [],
      view: ["services.read"],
      edit: ["services.read", "services.create", "services.update"],
      full: [
        "services.read",
        "services.create",
        "services.update",
        "services.delete",
      ],
    },
    special_actions: [],
  },

  // ══ Infrastructure ═════════════════════════════════════════════════════════
  {
    key: "servers",
    display_name: "Servers",
    category: "Infrastructure",
    description: "The server inventory, its tags and its Pulseway enrichment.",
    actions: [
      "servers.read",
      "servers.create",
      "servers.update",
      "servers.refresh",
      "servers.manage_tags",
      "servers.delete",
    ],
    access_levels: {
      none: [],
      view: ["servers.read"],
      edit: ["servers.read", "servers.create", "servers.update", "servers.refresh"],
      full: [
        "servers.read",
        "servers.create",
        "servers.update",
        "servers.refresh",
        "servers.manage_tags",
      ],
    },
    special_actions: [
      special(
        "servers.delete",
        "full",
        "Permanently delete a server",
        "Remove a server record. Not recoverable through the UI.",
      ),
    ],
  },
  {
    key: "containers",
    display_name: "Containers",
    category: "Infrastructure",
    description: "Container inventory collected from the Docker hosts.",
    // `classify` is the manual layer (Category / Excluded). Kept distinct from
    // `refresh` because they are genuinely different privileges — one records
    // how the team wants the fleet organised, the other pokes the Docker hosts —
    // even though both levels below grant them together.
    actions: ["containers.read", "containers.refresh", "containers.classify"],
    access_levels: {
      none: [],
      view: ["containers.read"],
      edit: ["containers.read", "containers.refresh", "containers.classify"],
      full: ["containers.read", "containers.refresh", "containers.classify"],
    },
    special_actions: [],
  },
  {
    key: "jenkins",
    display_name: "Jenkins",
    category: "Infrastructure",
    description: "Jenkins job inventory and manual triggering.",
    actions: ["jenkins.read", "jenkins.trigger"],
    access_levels: {
      none: [],
      view: ["jenkins.read"],
      edit: ["jenkins.read", "jenkins.trigger"],
      full: ["jenkins.read", "jenkins.trigger"],
    },
    special_actions: [],
  },

  // ══ Reporting ══════════════════════════════════════════════════════════════
  {
    key: "billing",
    display_name: "Billing & License Reconciliation",
    category: "Reporting",
    description:
      "Monthly reconciliation of vendor licence usage against the Priority SKU " +
      "catalogue, and the invoice import file it produces.",
    // Three of the six actions are SPECIAL rather than level-implied, and the
    // reason is the same in each case: they are the points where this module stops
    // describing data and starts affecting money.
    //   • import_approve — writes a new price list into the catalogue every future
    //     invoice is priced from;
    //   • snapshot_run   — reaches out to four vendor APIs with our credentials;
    //   • export         — produces the file Priority invoices customers from.
    // Uploading and mapping are ordinary `edit` work: an upload only stages a
    // preview, and a mapping is reviewable and reversible.
    actions: [
      "billing.read",
      "billing.upload",
      "billing.mappings_manage",
      "billing.import_approve",
      "billing.snapshot_run",
      "billing.export",
    ],
    access_levels: {
      none: [],
      view: ["billing.read"],
      edit: ["billing.read", "billing.upload", "billing.mappings_manage"],
      full: ["billing.read", "billing.upload", "billing.mappings_manage"],
    },
    special_actions: [
      special(
        "billing.import_approve",
        "edit",
        "Approve a SKU price-list import",
        "Apply a staged price list to the SKU catalogue. This changes the " +
          "catalogue every future invoice is priced from.",
      ),
      special(
        "billing.snapshot_run",
        "edit",
        "Run a monthly usage snapshot",
        "Collect billable usage from the vendor APIs (ESET, Acronis, " +
          "SentinelOne, Pulseway) with our own credentials.",
      ),
      special(
        "billing.export",
        "edit",
        "Export the Priority invoice file",
        "Produce the charge file Priority imports to invoice customers. The " +
          "output leaves the platform.",
      ),
    ],
  },
  {
    key: "reports",
    display_name: "Reports",
    category: "Reporting",
    description: "Ad-hoc reporting over the integrated systems, and saved templates.",
    actions: [
      "reports.read",
      "reports.generate",
      "reports.template_create",
      "reports.template_delete",
      "reports.export",
    ],
    access_levels: {
      none: [],
      view: ["reports.read"],
      edit: ["reports.read", "reports.generate", "reports.template_create"],
      full: [
        "reports.read",
        "reports.generate",
        "reports.template_create",
        "reports.template_delete",
      ],
    },
    special_actions: [
      // Generating renders results on screen; exporting produces a file that
      // leaves the platform. Deliberately separate, so generate never implies
      // export.
      special(
        "reports.export",
        "edit",
        "Export report results",
        "Download generated report results as a file (CSV or JSON).",
      ),
    ],
  },
  {
    key: "audits",
    display_name: "Customer Audits",
    category: "Reporting",
    description: "The customer audit programme: queue, schedule, runs and reports.",
    actions: [
      "audits.read",
      "audits.run",
      "audits.retry",
      "audits.schedule_manage",
      "audits.pulseway_sync",
      "audits.schedule_run_now",
      "audits.export_report",
      "audits.email_report",
      "audits.bulk_subscribe",
    ],
    access_levels: {
      none: [],
      view: ["audits.read"],
      edit: [
        "audits.read",
        "audits.run",
        "audits.retry",
        "audits.schedule_manage",
        "audits.pulseway_sync",
      ],
      full: [
        "audits.read",
        "audits.run",
        "audits.retry",
        "audits.schedule_manage",
        "audits.pulseway_sync",
        "audits.schedule_run_now",
      ],
    },
    special_actions: [
      special(
        "audits.export_report",
        "view",
        "Download audit reports",
        "Download a stored audit report as HTML or PDF.",
      ),
      special(
        "audits.email_report",
        "edit",
        "Email audit reports",
        "Send an audit report, including customer infrastructure detail, to an external recipient.",
      ),
      special(
        "audits.bulk_subscribe",
        "edit",
        "Bulk edit subscriptions",
        "Change audit-programme enrolment for many customers in one request.",
      ),
    ],
  },

  // ══ Integrations ═══════════════════════════════════════════════════════════
  {
    key: "copilot",
    display_name: "Microsoft 365 Copilot",
    category: "Integrations",
    description:
      "Chat with Microsoft 365 Copilot in the signed-in user's delegated context.",
    // `history` and `settings` are the user's OWN saved chats and defaults, so
    // they ride along with the ability to chat at every level rather than
    // forming a privilege ladder: withholding them from a `view` holder would
    // hand them a Copilot that forgets every conversation.
    actions: [
      "copilot.chat",
      "copilot.history",
      "copilot.settings",
      "copilot.mcp_integrations_read",
      "copilot.mcp_integrations_manage",
    ],
    access_levels: {
      none: [],
      view: ["copilot.chat", "copilot.history", "copilot.settings"],
      edit: ["copilot.chat", "copilot.history", "copilot.settings"],
      // NOT in `full`, deliberately. `full` is what an Operator holds, and it
      // is what "can use Copilot without restriction" means: chat, keep your
      // own history, set your own defaults. Deciding which business systems
      // exist, at which endpoint, with which OAuth application credentials and
      // which tools allowlisted, is not a wider version of using Copilot — it
      // decides what data can be put in front of a language model for
      // EVERYBODY. Making it a level would hand it to every Operator the moment
      // this shipped, which is precisely the escalation the split exists to
      // prevent. See the special actions below.
      full: ["copilot.chat", "copilot.history", "copilot.settings"],
    },
    special_actions: [
      // Two actions rather than one, and the read half is not merely
      // decoration. The admin LIST names every business system this deployment
      // reaches, its endpoint host and its OAuth client id — useful to somebody
      // auditing the platform who should not be able to re-point an endpoint.
      // Granting read without manage is a coherent position; the reverse is
      // not, so `manage` implies `read` wherever it is checked.
      special(
        "copilot.mcp_integrations_read",
        "view",
        "View business integrations",
        "See the global MCP integration catalog: endpoints, auth strategies and allowed tools. Reveals no secret.",
      ),
      special(
        "copilot.mcp_integrations_manage",
        "view",
        "Manage business integrations",
        "Create, edit, enable, disable and delete MCP integrations for everyone, including OAuth client credentials and the global tool allowlist.",
      ),
    ],
  },
  {
    key: "onboarding",
    display_name: "M365 Onboarding",
    category: "Integrations",
    description:
      "Microsoft 365 onboarding: assisted browser sessions, app registration and GDAP.",
    actions: [
      "onboarding.read",
      "onboarding.agent_report",
      "onboarding.session_launch",
      "onboarding.session_close",
      "onboarding.provision_app",
      "onboarding.read_app_secret",
      "onboarding.gdap_assign",
    ],
    access_levels: {
      none: [],
      view: ["onboarding.read"],
      edit: [
        "onboarding.read",
        "onboarding.session_launch",
        "onboarding.session_close",
      ],
      full: [
        "onboarding.read",
        "onboarding.session_launch",
        "onboarding.session_close",
      ],
    },
    special_actions: [
      // The browser agent's own callback surface: fetching its directives and
      // reporting progress. A SPECIAL action rather than part of a level, so no
      // human level confers it — only `svc_browser_agent` holds it.
      special(
        "onboarding.agent_report",
        "view",
        "Onboarding agent callbacks",
        "Fetch onboarding directives and report progress. Held by the browser agent service principal, not by people.",
      ),
      special(
        "onboarding.provision_app",
        "edit",
        "Provision integration app",
        "Create the integration app registration in a customer tenant, minting a client secret.",
      ),
      special(
        "onboarding.read_app_secret",
        "edit",
        "Read app secret",
        "Read the one-time client secret produced by provisioning.",
      ),
      special(
        "onboarding.gdap_assign",
        "edit",
        "Assign GDAP roles",
        "Grant delegated administrative rights into a customer tenant. This is a privilege grant.",
      ),
    ],
  },
  {
    key: "fortigate",
    display_name: "FortiGate API Check",
    category: "Integrations",
    description:
      "Monitor FortiGate API connectivity across the fleet, and validate a single device's credentials.",
    // The module gained a read surface with the monitoring programme (0028):
    // stored sweep history is ordinary data, so `view` now grants reading it.
    // Everything that reaches OUT — the single-device probe and the fleet sweep,
    // both performed with our credentials — still starts at `edit`.
    actions: [
      "fortigate.read",
      "fortigate.check",
      "fortigate.sweep_run",
      "fortigate.note_write",
      "fortigate.schedule_manage",
      "fortigate.send_report",
    ],
    // The two specials are deliberately absent from every level: they are
    // granted, never implied. Full still means "everything a level can give".
    access_levels: {
      none: [],
      view: ["fortigate.read"],
      // Writing a device note (0039) lands at `edit`: it reaches nothing outside
      // the platform, but it is shared state the next person's triage relies on.
      edit: [
        "fortigate.read",
        "fortigate.check",
        "fortigate.sweep_run",
        "fortigate.note_write",
      ],
      full: [
        "fortigate.read",
        "fortigate.check",
        "fortigate.sweep_run",
        "fortigate.note_write",
      ],
    },
    special_actions: [
      special(
        "fortigate.schedule_manage",
        "edit",
        "Manage the monitoring programme",
        "Change the daily sweep, the report cadence, and who the report is emailed to.",
      ),
      special(
        "fortigate.send_report",
        "edit",
        "Send the report on demand",
        "Email the API health report immediately. Mail leaves the platform.",
      ),
    ],
  },
  {
    key: "webhooks",
    display_name: "Webhooks",
    category: "Integrations",
    description: "Outbound webhook subscriptions and their delivery history.",
    actions: [
      "webhooks.read",
      "webhooks.create",
      "webhooks.update",
      "webhooks.toggle",
      "webhooks.test",
      "webhooks.replay",
      "webhooks.delete",
    ],
    access_levels: {
      none: [],
      view: ["webhooks.read"],
      edit: [
        "webhooks.read",
        "webhooks.create",
        "webhooks.update",
        "webhooks.toggle",
        "webhooks.test",
      ],
      full: [
        "webhooks.read",
        "webhooks.create",
        "webhooks.update",
        "webhooks.toggle",
        "webhooks.test",
        "webhooks.replay",
      ],
    },
    special_actions: [
      special(
        "webhooks.delete",
        "full",
        "Permanently delete a webhook",
        "Remove a webhook subscription and stop its deliveries.",
      ),
    ],
  },

  // ══ Identity ═══════════════════════════════════════════════════════════════
  {
    key: "users",
    display_name: "Users",
    category: "Identity",
    description: "Platform user directory and lifecycle.",
    actions: ["users.read", "users.create", "users.update", "users.disable", "users.delete"],
    access_levels: {
      none: [],
      view: ["users.read"],
      edit: ["users.read", "users.create", "users.update"],
      full: ["users.read", "users.create", "users.update", "users.disable"],
    },
    special_actions: [
      special(
        "users.delete",
        "full",
        "Delete a user",
        "Soft-delete a user, revoking their sessions and machine credentials.",
      ),
    ],
  },
  {
    key: "authorization",
    display_name: "Roles & Permissions",
    category: "Identity",
    description:
      "Role definitions, module access levels and special-action grants.",
    actions: [
      "authorization.read",
      "authorization.role_create",
      "authorization.role_update",
      "authorization.role_delete",
      "authorization.assign_roles",
    ],
    access_levels: {
      none: [],
      view: ["authorization.read"],
      edit: [
        "authorization.read",
        "authorization.role_create",
        "authorization.role_update",
      ],
      full: [
        "authorization.read",
        "authorization.role_create",
        "authorization.role_update",
      ],
    },
    special_actions: [
      special(
        "authorization.role_delete",
        "edit",
        "Delete a role",
        "Permanently remove a custom role.",
      ),
      special(
        "authorization.assign_roles",
        "view",
        "Assign roles to users",
        "Grant or remove a user's roles. Bounded further by the rule that nobody may confer authority they do not themselves hold.",
      ),
    ],
  },
  {
    key: "sessions",
    display_name: "Sessions",
    category: "Identity",
    description: "Server-side session inventory for platform users.",
    actions: ["sessions.read", "sessions.revoke"],
    access_levels: {
      none: [],
      view: ["sessions.read"],
      edit: ["sessions.read"],
      full: ["sessions.read"],
    },
    special_actions: [
      special(
        "sessions.revoke",
        "view",
        "Revoke sessions",
        "Forcibly sign another user out of one or all of their sessions.",
      ),
    ],
  },

  // ══ Governance ═════════════════════════════════════════════════════════════
  {
    key: "audit_log",
    display_name: "Audit Log",
    category: "Governance",
    // `audit.*` predates the module name; kept so stored grants need no rename.
    action_namespace: "audit",
    description: "The unified, append-only audit trail.",
    actions: ["audit.read", "audit.export", "audit.retention_manage"],
    access_levels: {
      none: [],
      view: ["audit.read"],
      edit: ["audit.read"],
      full: ["audit.read"],
    },
    special_actions: [
      special(
        "audit.export",
        "view",
        "Export the audit log",
        "Download the filtered audit trail as CSV or JSON.",
      ),
      special(
        "audit.retention_manage",
        "view",
        "Manage audit retention",
        "Set how long each category of log is kept. Its own special action, not " +
          "part of any level: deciding when audit history stops existing is a " +
          "different authority from reading or exporting it, and the one action " +
          "here whose effect cannot be undone.",
      ),
    ],
  },

  // ══ System ═════════════════════════════════════════════════════════════════
  {
    key: "settings",
    display_name: "Platform Settings",
    category: "System",
    description:
      "Operational platform configuration, security posture and integration status.",
    actions: ["settings.read", "settings.update", "settings.test_integration"],
    access_levels: {
      none: [],
      view: ["settings.read"],
      edit: ["settings.read", "settings.update", "settings.test_integration"],
      full: ["settings.read", "settings.update", "settings.test_integration"],
    },
    special_actions: [],
  },
  {
    key: "api_keys",
    display_name: "API Keys",
    category: "System",
    action_namespace: "apikeys",
    description: "Machine credentials: their scopes, status and security events.",
    actions: [
      "apikeys.read",
      "apikeys.read_events",
      "apikeys.update",
      "apikeys.toggle",
      "apikeys.set_scopes",
      "apikeys.create",
      "apikeys.rotate",
      "apikeys.revoke",
    ],
    access_levels: {
      none: [],
      view: ["apikeys.read", "apikeys.read_events"],
      edit: [
        "apikeys.read",
        "apikeys.read_events",
        "apikeys.update",
        "apikeys.toggle",
      ],
      full: [
        "apikeys.read",
        "apikeys.read_events",
        "apikeys.update",
        "apikeys.toggle",
        "apikeys.set_scopes",
      ],
    },
    special_actions: [
      special(
        "apikeys.create",
        "full",
        "Issue an API key",
        "Mint a new machine credential. The secret is shown once.",
      ),
      special(
        "apikeys.rotate",
        "full",
        "Rotate an API key",
        "Issue a replacement secret for an existing key.",
      ),
      special(
        "apikeys.revoke",
        "full",
        "Revoke an API key",
        "Permanently disable a machine credential.",
      ),
    ],
  },
  {
    key: "api_docs",
    display_name: "API Reference",
    category: "System",
    action_namespace: "docs",
    description:
      "The generated API reference and OpenAPI document for machine integrators.",
    actions: ["docs.read"],
    access_levels: {
      none: [],
      view: ["docs.read"],
      edit: ["docs.read"],
      full: ["docs.read"],
    },
    special_actions: [],
  },
] as const satisfies readonly ModuleDefinition[];

/** Every module key, as a literal union. */
export type ManifestModuleKey = (typeof MODULE_MANIFEST)[number]["key"];

/** Every action key defined anywhere in the manifest, as a literal union. */
export type ManifestActionKey =
  (typeof MODULE_MANIFEST)[number]["actions"][number];

/**
 * Special actions that confer another special action of the SAME module.
 *
 * One entry, and it is deliberately a table rather than a rule: holding
 * "manage business integrations" without "view business integrations" is not a
 * coherent position — a manager who could re-point an MCP endpoint but not list
 * the catalog would be editing blind — whereas the reverse (an auditor who may
 * read the catalog and not change it) is exactly why the pair was split in two.
 * So the implication runs one way only, and is written down rather than
 * inferred from the action names.
 *
 * Applied in `computeEffectiveAccess`, which is what makes it true of every
 * consumer at once: the guard, the `/me` payload the frontend reads, and the
 * effective-access view an administrator inspects. The implied action is still
 * subject to its own baseline, so this can confer an action but never a level.
 */
export const SPECIAL_ACTION_IMPLIES: Readonly<Record<string, readonly string[]>> = {
  "copilot.mcp_integrations_manage": ["copilot.mcp_integrations_read"],
};
